import InfoIcon from '@mui/icons-material/Info';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import {
  Autocomplete,
  Box,
  IconButton,
  ListItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { FC, useState } from 'react';
import { useUpdateTaskEstimateMutation } from 'redux/apiSlices/tasks.slice';

type TTaskEstimateProps = {
  estimate?: string;
  taskId: string;
};

type TPomodoroOptionProps = {
  props: React.HTMLAttributes<HTMLLIElement>;
  value: string;
};

const PomodoroOption: FC<TPomodoroOptionProps> = ({ props, value }) => {
  return (
    <ListItem
      {...props}
      sx={{
        py: 1,
        px: 2,
        '&:hover': {
          backgroundColor: 'action.hover',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" fontWeight={500}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          pomodoro{Number(value) > 1 ? 's' : ''} ({Number(value) * 25} min)
        </Typography>
      </Box>
    </ListItem>
  );
};

const TaskEstimate: FC<TTaskEstimateProps> = ({ estimate, taskId }) => {
  const [updateEstimate] = useUpdateTaskEstimateMutation();
  const onUpdateEstimate = async (value: string | null) => {
    await updateEstimate({
      taskId,
      estimate: value || '',
    });
  };

  const options = Array.from({ length: 16 }, (_, i) => String(i + 1));

  return (
    <Box sx={{ position: 'relative' }}>
      <Autocomplete
        value={estimate || null}
        options={options}
        onChange={async (event, newValue) => {
          await onUpdateEstimate(newValue);
        }}
        renderOption={(props, optionValue) => (
          <PomodoroOption props={props} value={optionValue} key={optionValue} />
        )}
        clearOnEscape
        fullWidth
        popupIcon={null}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'background.paper',
            py: 0,
            '& input': {
              py: 1,
              pl: 0.5,
              fontSize: '0.875rem',
            },
            '& input::placeholder': {
              color: 'text.secondary',
              opacity: 1,
            },
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'divider',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main',
          },
        }}
        renderInput={params => (
          <TextField
            {...params}
            placeholder="Set estimate"
            size="small"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <TimerOutlinedIcon
                  sx={{ fontSize: 18, ml: 1, color: 'text.secondary' }}
                />
              ),
              endAdornment: (
                <>
                  {params.InputProps.endAdornment}
                  <Tooltip title="Learn about Pomodoro technique">
                    <IconButton
                      onMouseDown={e => e.preventDefault()}
                      component="a"
                      href="https://workfeed.ai/blog/what-is-pomodoro-technique-in-time-management"
                      target="_blank"
                      size="small"
                      sx={{ mr: -0.5 }}
                    >
                      <InfoIcon sx={{ fontSize: 18, color: 'info.main' }} />
                    </IconButton>
                  </Tooltip>
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
};

export default TaskEstimate;
